export default class pegVerificationCode extends HTMLElement {
	connectedCallback() {
		this.abortController = new AbortController();

		this.startTime = Date.now();
		this.inputElement = this.querySelector('#code');
		this.#setEventListeners();
	}

	disconnectedCallback() {
		this.abortController.abort();
	}

	#setEventListeners() {
		this.querySelector('form').addEventListener('submit', (event) => {
			event.preventDefault();
		});

		this.inputElement.addEventListener(
			'focus',
			() => {
				this.#hideAllErrorMessages();
			},
			{ signal: this.abortController.signal },
		);

		this.inputElement.addEventListener(
			'input',
			(event) => {
				this.#removeNotWantedCharacters(event);
				this.#submitCode();
			},
			{ signal: this.abortController.signal },
		);

		this.inputElement.addEventListener(
			'paste',
			(event) => {
				this.#removeNotWantedCharacters(event);
			},
			{ signal: this.abortController.signal },
		);

		// prevent pasting in Chrome/iOS with the clipboard-button on the virtual keyboard
		this.inputElement.addEventListener(
			'beforeinput',
			(event) => {
				if (event.inputType === 'insertFromPaste' || event.inputType === 'insertFromPasteAsPlainText') {
					this.#removeNotWantedCharacters(event);
				}
			},
			{ signal: this.abortController.signal },
		);
	}

	#hideAllErrorMessages() {
		this.querySelectorAll(`.wdk-styledHintBox[data-error]`).forEach((hintBox) => {
			hintBox.classList.remove('is-visible');
		});
	}

	async #submitCode() {
		if (this.#isCodeComplete() && this.#canSubmitCode()) {
			this.querySelector('form').classList.add('submitInProgress');
			this.inputElement.setAttribute('readonly', true);

			try {
				const data = {
					_csrf: jPar.wdk.security.csrf.getToken(),
					code: this.inputElement.value,
				};
				const response = await fetchIt.post('/user/verification/oneTimePassword', JSON.stringify(data));
				this.#trackSubmitCode();

				if (jPar.util.network.hasValidSessionAndCsrfToken(response)) {
					if (response.verified) {
						this.#trackCodeValid();
						location.replace('/profile/welcomepage');
					} else {
						this.#handleUnverifiedCode(response.error);
						this.#trackCodeInvalid(response.error);
					}
				}
			} catch (error) {
				jPar.util.network.defaultErrorHandling(error);
			}
		}
	}

	/**
	 * @param {('CODE_INVALID'|'CODE_EXPIRED'|'GENERAL_ERROR')} errorCode - The error code indicating the type of error.
	 */
	#handleUnverifiedCode(errorCode) {
		this.querySelector(`.wdk-styledHintBox[data-error="${errorCode}"]`).classList.add('is-visible');
		this.querySelector('form').classList.remove('submitInProgress');
		this.inputElement.removeAttribute('readonly');
		this.inputElement.value = '';
		this.inputElement.blur();
	}

	#canSubmitCode() {
		return !this.querySelector('form').classList.contains('submitInProgress');
	}

	#isCodeComplete() {
		const maxLength = parseInt(this.inputElement.getAttribute('maxlength'), 10);
		const currentLength = this.inputElement.value.length;
		return maxLength > 0 && currentLength === maxLength;
	}

	#removeNotWantedCharacters(event) {
		event.preventDefault();
		this.inputElement.value = this.inputElement.value.replace(/\D/g, '');
	}

	#trackSubmitCode() {
		const elapsedSeconds = Math.round((Date.now() - this.startTime) / 1000); // time_in_seconds from start of the page to submit the form

		jPar.util.log.bigData({
			category: 'adaptive_blocking',
			subcategory: 'otp_verification_screen',
			action: 'submit',
			cd2: elapsedSeconds,
		});
	}

	#trackCodeValid() {
		jPar.util.log.bigData({
			category: 'adaptive_blocking',
			subcategory: 'otp_verification_screen',
			action: 'success',
		});
	}

	#trackCodeInvalid(errorCode) {
		jPar.util.log.bigData({
			category: 'adaptive_blocking',
			subcategory: 'otp_verification_screen',
			action: 'authentication_invalid',
			targetId: errorCode,
		});
	}
}
